import { useCallback } from 'react';
import { IRoutePermissions } from '../types/common';
import { Maybe } from '../types/types.generated';
import { useAuthUser } from './useAuthUser';
import { useFeatureToggles } from './useFeatureToggles';

/**
 * Hook to help with route operations
 */
export const useRouteUtils = () => {
  const { authUser, hasCapability, hasService } = useAuthUser();

  const { isFeatureToggledOn } = useFeatureToggles();

  /**
   * Check if user has permissions to view route
   */
  const checkRoutePermissions = useCallback(
    (routePermissions: Maybe<IRoutePermissions> | undefined) => {
      if (!authUser) {
        return false;
      }

      if (!routePermissions) {
        return true;
      }

      const { userCapabilities, featureToggle, customRule } = routePermissions;

      if (customRule) {
        // If custom check is provided, its result overrides everything
        return customRule({
          authUser,
          hasCapability,
          hasService,
          isFeatureToggledOn,
        });
      }

      if (userCapabilities && !hasCapability(userCapabilities)) {
        return false;
      }

      return !(featureToggle && !isFeatureToggledOn(featureToggle));
    },
    [authUser, hasCapability, isFeatureToggledOn, hasService]
  );

  return { checkRoutePermissions };
};
